import React, { useState } from "react";

import * as api from "../../api";
import { creditPreview, secondsToMinutes } from "../../logic";
import Button from "../Button";
import Modal from "../Modal";

const inputStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink-900)",
  background: "#fff",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  padding: "10px 12px",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: 12.5,
  color: "var(--ink-700)",
  marginBottom: 6,
};

/** Adjust credits — form → review (before→after) → confirm. Overdraw is refused
 * server-side; the client preview is advisory. */
export default function CreditModal({
  orgName,
  balanceSeconds,
  onCancel,
  onConfirm,
}: {
  orgName: string;
  balanceSeconds: number;
  onCancel: () => void;
  onConfirm: (direction: api.CreditDirection, minutes: number, reason: string) => Promise<void>;
}) {
  const [step, setStep] = useState<"form" | "review">("form");
  const [direction, setDirection] = useState<api.CreditDirection>("add");
  const [minutes, setMinutes] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mins = Number(minutes);
  const curMin = secondsToMinutes(balanceSeconds);
  const preview = creditPreview(balanceSeconds, direction, mins);

  const review = () => {
    if (!(Number.isInteger(mins) && mins > 0)) {
      setErr("Enter a whole number of minutes greater than 0.");
      return;
    }
    if (!reason.trim()) {
      setErr("A reason is required.");
      return;
    }
    setErr(null);
    setStep("review");
  };

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(direction, mins, reason.trim());
    } catch (e: any) {
      setErr(e?.message || "Adjustment failed");
      setBusy(false);
      setStep("form");
    }
  };

  return (
    <Modal title="Adjust credits" onClose={onCancel}>
      {step === "form" ? (
        <>
          <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "var(--text-body)", lineHeight: 1.55 }}>
            Adjust prepaid call credits for <b>{orgName}</b>. Current balance {curMin} min.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Direction</label>
              <select value={direction} onChange={(e) => setDirection(e.target.value as api.CreditDirection)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="add">Add</option>
                <option value="subtract">Subtract</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Minutes</label>
              <input type="number" min={1} step={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="0" style={{ ...inputStyle, fontFamily: "var(--font-mono)" }} />
            </div>
          </div>
          <label style={labelStyle}>Reason <span style={{ color: "var(--danger-500)" }}>(required, audited)</span></label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Goodwill credit for outage" style={inputStyle} />
          {err && <p style={{ margin: "8px 0 0", color: "var(--danger-500)", fontSize: 12.5 }}>{err}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={review}>Review</Button>
          </div>
        </>
      ) : (
        <>
          <div style={{ border: "1px solid var(--border-hairline)", borderRadius: 12, overflow: "hidden", marginBottom: 18 }}>
            {[
              { k: "Org", v: orgName },
              { k: "Action", v: `${direction === "add" ? "Add" : "Subtract"} ${mins} min` },
              { k: "Reason", v: reason.trim() },
              { k: "Balance", v: `${curMin} → ${preview.afterMinutes} min` },
            ].map((r) => (
              <div key={r.k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "11px 14px", borderBottom: "1px solid var(--border-hairline)" }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)" }}>{r.k}</span>
                <span style={{ fontSize: 13.5, color: "var(--ink-900)", fontWeight: 600, textAlign: "right" }}>{r.v}</span>
              </div>
            ))}
          </div>
          {preview.overdraw && (
            <p style={{ margin: "0 0 12px", color: "#8A5209", fontSize: 12.5 }}>
              Heads up: this would overdraw the balance — the server will refuse it.
            </p>
          )}
          {err && <p style={{ margin: "0 0 12px", color: "var(--danger-500)", fontSize: 12.5 }}>{err}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="ghost" onClick={() => { setErr(null); setStep("form"); }}>Back</Button>
            <Button variant="primary" onClick={confirm} disabled={busy}>{busy ? "Working…" : "Confirm adjustment"}</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
